export type HeaderKey = "id" | "title" | "excerpt" | "permalink" | "url";

const HEADER_ALIASES: Record<HeaderKey, string[]> = {
  id: ["id", "external id"],
  title: ["title", "name"],
  excerpt: ["excerpt", "description"],
  permalink: ["permalink", "source"],
  url: ["url", "imageurl"],
};

const REQUIRED_HEADERS: HeaderKey[] = ["title", "excerpt", "url"];

const HEADER_MATCH_LABELS: Record<HeaderKey, string> = {
  id: "ID / External Id",
  title: "Title / Name",
  excerpt: "Excerpt / Description",
  permalink: "Permalink / Source",
  url: "URL / imageUrl",
};

export const PREVIEW_HEADERS = ["ID", "Title", "Excerpt", "Permalink", "URL"];

type HeaderIndexMap = Partial<Record<HeaderKey, number>>;

export type CsvBandRow = {
  id: string;
  title: string;
  excerpt: string;
  permalink: string;
  url: string;
};

export type ImportBandPayloadRow = {
  externalId: string;
  name: string;
  description: string;
  imageUrl: string;
  organisationId: string;
  source: string;
};

export type ParseBandCsvResult = {
  headerRow: string[];
  rows: CsvBandRow[];
  warnings: string[];
  rawRowCount: number;
  error: string | null;
};

const stripBom = (value: string) => value.replace(/^\uFEFF/, "");

const sanitiseCell = (value: string | null | undefined) =>
  stripBom((value ?? "").toString()).trim();

const normaliseHeader = (value: string) => sanitiseCell(value).toLowerCase();

const resolveHeaderMap = (headers: string[]) => {
  const headerIndex: HeaderIndexMap = {};
  const duplicateKeySet = new Set<HeaderKey>();

  headers.forEach((header, index) => {
    const normalised = normaliseHeader(header);
    const matchedKey = (Object.keys(HEADER_ALIASES) as HeaderKey[]).find(
      (key) => HEADER_ALIASES[key].some((alias) => alias === normalised)
    );

    if (!matchedKey) {
      return;
    }

    if (headerIndex[matchedKey] !== undefined) {
      duplicateKeySet.add(matchedKey);
      return;
    }

    headerIndex[matchedKey] = index;
  });

  const missingRequired = REQUIRED_HEADERS.filter(
    (key) => headerIndex[key] === undefined
  );

  return {
    headerIndex,
    duplicateKeys: Array.from(duplicateKeySet),
    missingRequired,
  };
};

const buildRows = (
  dataRows: string[][],
  headerIndex: HeaderIndexMap
): { rows: CsvBandRow[]; warnings: string[]; error: string | null } => {
  const rows: CsvBandRow[] = [];
  const warnings: string[] = [];

  const getCell = (row: string[], key: HeaderKey) => {
    const columnIndex = headerIndex[key];
    if (columnIndex === undefined) {
      return "";
    }
    return sanitiseCell(row[columnIndex]);
  };

  for (let index = 0; index < dataRows.length; index += 1) {
    const row = dataRows[index];

    if (!Array.isArray(row)) {
      warnings.push(`Row ${index + 2} is invalid and has been skipped.`);
      continue;
    }

    const idRaw = getCell(row, "id");
    const title = getCell(row, "title");
    const excerpt = getCell(row, "excerpt");
    const permalink = getCell(row, "permalink");
    const url = getCell(row, "url");

    if ([idRaw, title, excerpt, permalink, url].every((value) => !value)) {
      continue;
    }

    if (!title) {
      return {
        rows: [],
        warnings: [],
        error: `Row ${index + 2} is missing a value for Title / Name.`,
      };
    }

    const resolvedId = idRaw || title;

    rows.push({
      id: resolvedId,
      title,
      excerpt,
      permalink,
      url,
    });
  }

  return { rows, warnings, error: null };
};

export const parseBandCsvTableRows = (
  tableRows: string[][]
): ParseBandCsvResult => {
  if (tableRows.length === 0) {
    return {
      headerRow: [],
      rows: [],
      warnings: [],
      rawRowCount: 0,
      error: "The CSV file is empty.",
    };
  }

  const rawHeaderRow = tableRows[0] ?? [];
  const headerRow = rawHeaderRow.map((value) => sanitiseCell(value));

  const { headerIndex, duplicateKeys, missingRequired } =
    resolveHeaderMap(headerRow);

  if (duplicateKeys.length > 0) {
    return {
      headerRow,
      rows: [],
      warnings: [],
      rawRowCount: 0,
      error: `Duplicate columns detected: ${duplicateKeys
        .map((key) => HEADER_MATCH_LABELS[key])
        .join(", ")}.`,
    };
  }

  if (missingRequired.length > 0) {
    return {
      headerRow,
      rows: [],
      warnings: [],
      rawRowCount: 0,
      error: `Missing required columns: ${missingRequired
        .map((key) => HEADER_MATCH_LABELS[key])
        .join(", ")}`,
    };
  }

  const dataRows = tableRows.slice(1);
  const rawRowCount = dataRows.length;
  const { rows, warnings, error } = buildRows(dataRows, headerIndex);

  if (error) {
    return {
      headerRow,
      rows: [],
      warnings: [],
      rawRowCount,
      error,
    };
  }

  if (rows.length === 0) {
    return {
      headerRow,
      rows: [],
      warnings,
      rawRowCount,
      error:
        "No data rows were found after the header. Please check the CSV content.",
    };
  }

  return {
    headerRow,
    rows,
    warnings,
    rawRowCount,
    error: null,
  };
};

export const buildBandImportPayload = (
  rows: CsvBandRow[],
  organisationId: string
): ImportBandPayloadRow[] =>
  rows.map((row) => ({
    externalId: row.id,
    name: row.title,
    description: row.excerpt || "",
    imageUrl: row.url || "",
    organisationId,
    source: row.permalink || row.id,
  }));
